import React from 'react';
import { Instagram, Linkedin } from 'lucide-react';
import { ConvergeLogo, ConvergeMark, EvalentXLogo } from './ConvergeLogo';
import { RegisterButton } from './RegisterButton';
import { NAV, VENUE, ADDRESS } from './data';

const ShareLink = () => {
  const [copied, setCopied] = React.useState(false);
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/converge`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { window.prompt('Copy this link:', url); }
  };
  return (
    <button type="button" onClick={copy} className="mt-4 block text-xs font-bold uppercase tracking-wider underline decoration-[#D7FF38] underline-offset-4" data-testid="footer-share-link-btn">
      {copied ? 'Share link copied ✓' : 'Copy share link (with preview card)'}
    </button>
  );
};

export const ConvergeFinalCta = () => (
  <section className="bg-[#D7FF38] border-b-2 border-[#111] relative overflow-hidden" data-testid="final-cta-section">
    <ConvergeMark size={360} variant="mono" className="absolute -left-20 -bottom-24 opacity-10 cv-spin pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-left">
      <h2 className="cv-display font-extrabold uppercase leading-[0.86] text-5xl sm:text-7xl lg:text-[110px] max-w-5xl" data-testid="final-cta-title">Your next big idea starts here.</h2>
      <p className="mt-6 text-base sm:text-lg font-bold">Build boldly. Connect meaningfully. Create what comes next.</p>
      <RegisterButton label="REGISTER FOR CONVERGE →" tone="ink" size="lg" testId="final-register-btn" className="mt-10" />
    </div>
  </section>
);

export const ConvergeFooter = () => (
  <footer className="bg-[#111] text-[#F5F3EB]" data-testid="converge-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <ConvergeLogo variant="inverse" />
        <p className="mt-6 cv-display text-2xl font-extrabold uppercase">16–17 November 2026</p>
        <p className="mt-4 text-sm font-extrabold uppercase tracking-wider" data-testid="footer-brand-line">CONVERGE by EvalentX</p>
        <p className="mt-5 text-sm font-bold">{VENUE}</p>
        <p className="mt-1 text-sm text-[#F5F3EB]/70 max-w-sm" data-testid="footer-address">{ADDRESS}</p>
      </div>
      <nav className="md:col-span-3" aria-label="Footer">
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D7FF38]">Explore</p>
        <ul className="mt-4 space-y-2">
          {NAV.map(([label, id]) => <li key={id}><a href={`#${id}`} className="text-sm hover:text-[#D7FF38] transition-colors" data-testid={`footer-link-${id}`}>{label}</a></li>)}
        </ul>
      </nav>
      <div className="md:col-span-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D7FF38]">Organised by</p>
        <a href="https://evalentx.com" target="_blank" rel="noopener noreferrer" className="mt-4 inline-block" aria-label="EvalentX website" data-testid="footer-evalentx-link">
          <EvalentXLogo size="md" inverted noLink />
        </a>
        <p className="mt-3 text-sm"><a href="https://evalentx.com" target="_blank" rel="noopener noreferrer" className="underline decoration-[#D7FF38] underline-offset-4">evalentx.com</a></p>
        <div className="mt-7 pt-6 border-t border-[#F5F3EB]/15" data-testid="footer-association">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D7FF38]">In association with AARUUSH</p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
          <img src="/aaruush-logo.webp" alt="AARUUSH" className="h-10 sm:h-12 w-auto max-w-full object-contain object-left" width="256" height="48" loading="lazy" data-testid="footer-aaruush-logo" />
            <div className="flex items-center gap-2" data-testid="footer-aaruush-socials">
              <a href="https://www.instagram.com/aaruush_srm" target="_blank" rel="noopener noreferrer" aria-label="AARUUSH on Instagram"
                className="w-10 h-10 flex items-center justify-center border-2 border-[#F5F3EB] text-[#F5F3EB] transition-colors hover:bg-[#D7FF38] hover:text-[#111] hover:border-[#D7FF38]" data-testid="footer-aaruush-instagram">
                <Instagram size={18} />
              </a>
              <a href="https://www.linkedin.com/company/aaruush-srm-ist" target="_blank" rel="noopener noreferrer" aria-label="AARUUSH on LinkedIn"
                className="w-10 h-10 flex items-center justify-center border-2 border-[#F5F3EB] text-[#F5F3EB] transition-colors hover:bg-[#D7FF38] hover:text-[#111] hover:border-[#D7FF38]" data-testid="footer-aaruush-linkedin">
                <Linkedin size={18} />
              </a>
            </div>
          </div>
        </div>
        <RegisterButton label="REGISTER NOW ↗" size="sm" testId="footer-register-btn" className="mt-6" />
        <ShareLink />
      </div>
    </div>
    <div className="border-t border-[#F5F3EB]/15">
      <p className="max-w-7xl mx-auto px-4 sm:px-6 py-5 text-[11px] text-[#F5F3EB]/60">© 2026 EvalentX. CONVERGE by EvalentX · In association with AARUUSH · {VENUE}.</p>
    </div>
  </footer>
);
